import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { matchPath } from 'react-router';

import { withRouter } from 'core/libs/router';
import { compose } from 'core/libs/recompose';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { getCurrentMenuMatches } from 'core/utils/url-helper';
import themePropTypes from 'core/utils/prop-types/theme';

import withTheme from 'core/hocs/withTheme';
import withMenu from 'core/hocs/withMenu';
import { topicAmp as topicAmpPattern } from 'core/components/BaseRoutes/routes-patterns';

import Logo from 'core/components/Logo';
import Link from 'core/components/Link';
import { Block, Section } from 'core/components/Grid';
import { Desktop } from 'core/components/breakpoint';
import Menu from 'core/components/ScooterMenu';

import Social from './Social';


const emptyItemsRef = [];

/**
 * @author Aleksey Dibrivniy <a.dibrivniy@rambler-co.ru>
 */
function Scooter(props, context) {
  const {
    /** Дополнительные пункты меню (нужно для indicator)*/
    additionalMenuItems,
    /** Номер индекса начиная с которого включаются доп пункты меню */
    additionalMenuItemsIndex,
    services,
    contacts,
    commercial,
    menuListClassName,
    menu,
    theme: {
      texts: {
        caption3: {
          font: caption3Font,
        },
      },
      colors: {
        hint: hintColor,
      },
      controls: {
        scooter: scooterAtoms,
      },
    },
    theme,
    location,
  } = props;

  const {
    erratumUrl,
    socials: socialLinks,
  } = context;

  /* удаляем пробелы/дефисы/скобоки, для корректного задания атрибута href="tel:+7..." */
  const phoneFormatted = contacts.phone && contacts.phone.body.replace(/[^\d+]+/g, '');
  const year = (new Date()).getFullYear();

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .link
          font ${caption3Font}
        .tel
          white-space nowrap
      `}</style>
    </scope>
  );

  let items = menu?.data?.attributes.menu_items ?? emptyItemsRef;
  if (items && additionalMenuItems) {
    if (additionalMenuItemsIndex) {
      items = items.slice(); // делаем копию потому что splice мутирует обьект
      items.splice(additionalMenuItemsIndex, 0, ...additionalMenuItems);
    } else {
      items = items.concat(additionalMenuItems);
    }
  }

  const isAmp = !!matchPath(location.pathname, topicAmpPattern);
  const currentMatches = useMemo(
    () => getCurrentMenuMatches(items, location, isAmp),
    [location, items, isAmp]
  );
  const currentItem = currentMatches[0];

  return (
    <footer className='wrapper'>
      <style jsx>{`
        .header
          :global(.desktop) &
            display flex
            align-items center
            justify-content space-between

        .footer
          padding 20px 0
          max-height 1000px // disable font boosting

        .commercial
          :global(.mobile) &
            padding-top 24px

        .commercial-item
          margin 0
          & + &
            margin-top 10px

        .contacts
          :global(.desktop) &
            text-align right
          :global(.mobile) &
            margin 24px 0

        .contacts-item
          margin 10px 0 0
          &:first-child
            margin-top 0
          :global(.mobile) &
            margin 10px 0 0

        .contacts-age
          display inline-block
          margin-left 5px
          padding 2px 10px
          border-radius 5px
      `}</style>
      <style jsx>{`
        .header
          padding ${scooterAtoms.header.padding}
          border-top ${scooterAtoms.header.borderTop}
        .wrapper
          background ${scooterAtoms.background}
          padding ${scooterAtoms.padding}
        .commercial
          border-top ${scooterAtoms.commercial.border}
        .text
          font ${caption3Font}
          color ${hintColor}
        .contacts-age
          border 1px solid ${hintColor}
        .services-item + .services-item
          margin-top ${scooterAtoms.servicesItem.marginTop}
      `}</style>
      <div className='header'>
        <Logo atomsName='scooter' />
        {socialLinks && socialLinks.length > 0 && <Social theme={theme} />}
      </div>
      <Menu
        menu={menu}
        currentItem={currentItem}
        additionalMenuItems={additionalMenuItems}
        additionalMenuItemsIndex={additionalMenuItemsIndex}
        menuListClassName={menuListClassName}
        isAmp={isAmp}
        theme={theme}
      />
      <div className='footer'>
        <Section>
          <Block width={3} mobile={12}>
            <div className='services'>
              {[].concat(services || []).map((item, index) => (
                <div key={index} className='services-item'>
                  <Link
                    to={item.link}
                    type='tertiary'
                    className={scoped.wrapClassNames('link')}
                    isCurrent={currentItem?.link === item.link}
                  >
                    {item.content}
                  </Link>
                </div>
              ))}
            </div>
          </Block>
          <Block
            width={3}
            mobile={12}
            reorderDesktop={3}
          >
            <div className='contacts'>
              <p className={cx('contacts-item', 'text')}>
                {`${contacts.site}, ${year} г. `}
                {contacts.age && (
                  <span className='contacts-age'>{contacts.age + '+'}</span>
                )}
              </p>
              {erratumUrl && (
                <Desktop>
                  <p className={cx('contacts-item', 'text')}>
                    {contacts.erratum || 'Нашли опечатку? Выделите текст и нажмите Ctrl+Enter'}
                  </p>
                </Desktop>
              )}
              {contacts.phone && (
                <p className={cx('contacts-item', 'text')}>
                  {contacts.phone.caption + ' '}
                  <Link
                    to={`tel:${phoneFormatted}`}
                    type='tertiary'
                    className={scoped.wrapClassNames('link', 'tel')}
                  >
                    {contacts.phone.body}
                  </Link>
                </p>
              )}
            </div>
          </Block>
          <Block width={6} mobile={12}>
            <div className='commercial'>
              {[].concat(commercial || []).map((item, index) => {
                return (
                  <div key={index} className={cx('commercial-item', 'text')}>
                    {typeof item === 'object' ? (
                      <div>
                        {item.caption}
                        {item.body}
                      </div>
                    ) : item}
                  </div>
                );
              })}
            </div>
          </Block>
        </Section>
      </div>
      <scoped.styles />
    </footer>
  );
}

const info = PropTypes.oneOfType([
  PropTypes.node,
  PropTypes.shape({
    caption: PropTypes.node,
    body: PropTypes.node,
  }),
]);

const linkStateContract = `{
  color,
  decoration
}`;

const linkContract = `{
  font,
  textTransform,
  letterSpacing,
  idle: ${linkStateContract},
  hover: ${linkStateContract},
  active: ${linkStateContract},
  current: ${linkStateContract},
}`;

const socialStates = `{
  color,
  background,
  border,
} `;

Scooter.propTypes = {
  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },
      controls: {
        scooter: {
          padding,
          menuLink: ${linkContract},
          logo: {
            Icon,
            width,
          },
          commercial: {
            border,
          },
          border,
          background,
          social: {
            idle: ${socialStates},
            hover: ${socialStates},
            margin,
            size,
            iconSize,
            borderRadius,
          },
          servicesItem: {
            marginTop
          },
        }
      }
    }
  `),
  /** Дополнительные пункты меню */
  additionalMenuItems: PropTypes.array,
  /** Номер индекса начиная с которого включаються доп пункты меню */
  additionalMenuItemsIndex: PropTypes.number,
  /** Дополнительные сервисные ссылки */
  services: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          pathname: PropTypes.string.isRequired,
          state: PropTypes.object,
        }),
      ]).isRequired,
      body: PropTypes.string,
      icon: PropTypes.func,
    })
  ).isRequired,
  /** Коммерческая информация */
  commercial: PropTypes.arrayOf(info),
  /** Контактные данные издания */
  contacts: PropTypes.objectOf(info),
  /** @ignore */
  menuListClassName: PropTypes.string,
  /** @ignore */
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  /** Меню для футера */
  menu: PropTypes.object,
};

Scooter.contextTypes = {
  erratumUrl: PropTypes.string,
  socials: PropTypes.array,
};

const ScooterComponentWithHOCs = compose(
  withTheme,
  withMenu('footer'),
  withRouter,
)(Scooter);
ScooterComponentWithHOCs.displayName = 'Scooter';

export { Scooter as StorybookComponent };
export default ScooterComponentWithHOCs;
